import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Help,
    Settings,
    Email,
    Groups,
    HowToReg,
    BarChart,
    Summarize,
    Dashboard,
    EditCalendar
} from '@mui/icons-material';

export default function useEventSidebar() {
    const location = useLocation();
    const { eventSlug } = useParams();
    const { eventData } = useSelector((state) => state.event);

    const currentPath = location.pathname;
    const slug = `/event/${eventSlug}`;

    // variable to handle new event creation
    const isNewEvent = useMemo(() => eventSlug === 'new', [eventSlug]);

    const enabledMenus = useMemo(() => {
        // 1. If eventSlug is "new" - only event details will be enabled
        if (isNewEvent) {
            return {
                dashboard: false,
                eventDetails: true,
                tickets: false,
                team: false,
                invitations: false,
                registrations: false,
                analytics: false,
                reports: false
            };
        }

        // 2. If eventData was published before - enable all menus
        if (eventData?.was_published_before) {
            return {
                dashboard: true,
                eventDetails: true,
                tickets: true,
                team: true,
                invitations: true,
                registrations: true,
                analytics: true,
                reports: true
            };
        }

        // 3. If in eventData is_publishable?.schedule === 1 then enable ticket menu
        if (eventData?.is_publishable?.schedule === 1) {
            return {
                dashboard: true,
                eventDetails: true,
                tickets: true,
                team: false,
                invitations: false,
                registrations: false,
                analytics: false,
                reports: false
            };
        }

        // 4. If eventData has eventId then only enabled dashboard menu
        if (eventData?.eventId) {
            return {
                dashboard: true,
                eventDetails: true,
                tickets: false,
                team: false,
                invitations: false,
                registrations: false,
                analytics: false,
                reports: false
            };
        }

        // By default only event detail menu will be enabled
        return {
            dashboard: false,
            eventDetails: true,
            tickets: false,
            team: false,
            invitations: false,
            registrations: false,
            analytics: false,
            reports: false
        };
    }, [eventData, isNewEvent]);

    const eventDashboardMenusList = [
        {
            id: 'eventDashboard',
            title: 'Event Dashboard',
            type: 'item',
            icon: <Dashboard />,
            url: `${slug}/dashboard`,
            enabled: enabledMenus.dashboard
        },
        {
            id: 'manageDetails',
            title: 'Manage Details',
            type: 'collapse',
            icon: <EditCalendar />,
            children: [
                {
                    id: 'eventDetails',
                    title: 'Event details',
                    type: 'item',
                    url: `${slug}/event-details`,
                    enabled: enabledMenus.eventDetails
                },
                {
                    id: 'tickets',
                    title: 'Tickets',
                    type: 'item',
                    url: `${slug}/tickets`,
                    enabled: enabledMenus.tickets
                }
            ]
        },
        {
            id: 'team',
            title: 'Team',
            type: 'item',
            icon: <Groups />,
            url: `${slug}/team`,
            enabled: enabledMenus.team
        },
        {
            id: 'invitations',
            title: 'Invitations',
            type: 'item',
            icon: <Email />,
            url: `${slug}/invitations`,
            enabled: enabledMenus.invitations
        },
        {
            id: 'registrations',
            title: 'Registrations',
            type: 'item',
            icon: <HowToReg />,
            url: `${slug}/registrations`,
            enabled: enabledMenus.registrations
        },
        {
            id: 'analytics',
            title: 'Analytics',
            type: 'item',
            icon: <BarChart />,
            url: `${slug}/analytics`,
            enabled: enabledMenus.analytics
        },
        {
            id: 'reports',
            title: 'Reports',
            type: 'item',
            icon: <Summarize />,
            url: `${slug}/reports`,
            enabled: enabledMenus.reports
        },
        {
            id: 'help-support',
            title: 'Help & Support',
            type: 'item',
            icon: <Help />,
            url: `${slug}/help`,
            enabled: true // Always enabled
        },
        {
            id: 'settings',
            title: 'Event Settings',
            type: 'item',
            icon: <Settings />,
            url: `${slug}/settings`,
            enabled: true
        }
    ];

    // Filter tabs for each container
    const mainTabs = eventDashboardMenusList.filter(
        (item) => !['settings', 'help-support'].includes(item.id)
    );

    const settingTabs = eventDashboardMenusList.filter((item) =>
        ['help-support', 'settings'].includes(item.id)
    );

    const selectedItems = useMemo(() => {
        // Find if the current path matched any of in the main tabs
        const mainSelected = mainTabs.find(
            (item) =>
                item.url === currentPath ||
                (item.children && item.children.some((child) => child.url === currentPath))
        );

        // Find if the current path matched any of in the setting tabs
        const settingSelected = settingTabs.find((item) => item.url === currentPath);

        return {
            mainSelected: mainSelected?.id || '',
            settingSelected: settingSelected?.id || '',
            childSelectedUrl: currentPath
        };
    }, [currentPath]);

    const isItemSelected = (item) => {
        if (item.type === 'item') {
            return item.url === selectedItems.childSelectedUrl;
        } else if (item.type === 'collapse') {
            return item.id === selectedItems.mainSelected;
        } else if (item.id === 'settings') {
            return item.id === selectedItems.settingSelected;
        }
        return false;
    };

    return {
        mainTabs,
        isItemSelected,
        selectedItems,
        settingTabs
    };
}
